import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "./AuthContext";
import { Save, Bold, Italic, Underline, Link2, Image as ImageIcon, Loader2, Trash2, Eye } from "lucide-react";

// v68.24 — defense-in-depth: even though the backend sanitises with bleach on
// write, we also DOMPurify at every render boundary so a compromised DB row
// or a future API-writer bug can't inject script into an admin's browser.
const SAFE_HTML_CFG = {
  ALLOWED_TAGS: ["a", "b", "br", "div", "em", "i", "img", "p", "span", "strong", "u"],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "style", "width", "height"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,):|\/|#)/i,
};
function safeHtml(dirty) { return DOMPurify.sanitize(dirty || "", SAFE_HTML_CFG); }

/**
 * v68.20 — Rich Email Signature editor.
 *
 * Uses a contentEditable div for the rich text (basic toolbar), and a hidden
 * <input type=file> for the logo upload. The backend sanitises the HTML on
 * save so the toolbar is intentionally minimal.
 *
 * Signature is appended to every outbound email routed through
 * `send_with_logging(from_user_id=...)` — currently the reply-from-triage
 * path and the generic outbound composer.
 */
export default function EmailSignatureSection() {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [state, setState] = useState({
    email_signature_html: "",
    email_signature_text: "",
    email_signature_logo_url: "",
    email_signature_enabled: true,
  });

  // Initial load
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API_BASE}/users/me/signature`)
      .then((r) => {
        if (cancel) return;
        setState(r.data);
        if (editorRef.current) editorRef.current.innerHTML = safeHtml(r.data.email_signature_html);
      })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const html = editorRef.current?.innerHTML || "";
      const { data } = await axios.patch(`${API_BASE}/users/me/signature`, {
        email_signature_html: html,
        email_signature_enabled: state.email_signature_enabled,
      });
      setState(data);
      if (editorRef.current) editorRef.current.innerHTML = safeHtml(data.email_signature_html);
      toast.success("Signature saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onUploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API_BASE}/users/me/signature/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState((s) => ({ ...s, email_signature_logo_url: data.email_signature_logo_url }));
      toast.success("Logo updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onClearLogo = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/users/me/signature`, { clear_logo: true });
      setState(data);
      toast.success("Logo removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };
  const insertBadgeExample = () => {
    // Convenience: paste a common "Xero Certified Advisor"-style badge shell
    document.execCommand("insertHTML", false,
      '<span style="display:inline-block;padding:3px 8px;background:#eef2ff;color:#3730a3;font-size:12px;border-radius:9999px;">Xero Certified</span>&nbsp;');
    editorRef.current?.focus();
  };

  const onToggleEnabled = async () => {
    const next = !state.email_signature_enabled;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/users/me/signature`, { email_signature_enabled: next });
      setState(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <section className="rounded-lg border p-6 space-y-4" style={{ borderColor: "var(--border)", background: "var(--paper)" }} data-testid="email-signature-section">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg text-[var(--ink)]" style={{ fontWeight: 500 }}>Email signature</h2>
          <p className="text-[12px] text-[var(--ink-2)] max-w-lg mt-1">
            Appended to every outbound email you send from Practice OS. Add a logo, links, and badges
            to build trust with your clients.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer" data-testid="signature-enabled-toggle">
          <input type="checkbox" checked={state.email_signature_enabled}
                 onChange={onToggleEnabled} disabled={saving} />
          <span className="text-[var(--ink-2)]">Enabled</span>
        </label>
      </header>

      {/* Logo row */}
      <div className="flex items-center gap-4">
        <div className="w-28 h-16 flex items-center justify-center rounded border overflow-hidden"
             style={{ borderColor: "var(--border)", background: "var(--bg)" }}
             data-testid="signature-logo-preview">
          {state.email_signature_logo_url ? (
            <img src={state.email_signature_logo_url} alt="signature logo"
                 style={{ maxWidth: "100%", maxHeight: "100%" }} />
          ) : (
            <span className="text-[10px] text-[var(--ink-3)]">no logo</span>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="signature-upload-logo"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] border hover:bg-[var(--subtle)] disabled:opacity-50"
            style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
            {state.email_signature_logo_url ? "Replace logo" : "Upload logo / photo"}
          </button>
          {state.email_signature_logo_url && (
            <button
              type="button" onClick={onClearLogo} disabled={saving}
              data-testid="signature-clear-logo"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] text-[var(--ink-3)] hover:text-[var(--accent)]"
            >
              <Trash2 size={12} /> Remove
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                 className="hidden" onChange={(e) => onUploadLogo(e.target.files?.[0])} />
          <span className="text-[10px] text-[var(--ink-3)]">PNG / JPG / GIF / WEBP / SVG · max 250 KB</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1 border rounded-t-md px-2 py-1"
           style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <ToolbarBtn onClick={() => exec("bold")} label="Bold" icon={Bold} testid="signature-btn-bold" />
        <ToolbarBtn onClick={() => exec("italic")} label="Italic" icon={Italic} testid="signature-btn-italic" />
        <ToolbarBtn onClick={() => exec("underline")} label="Underline" icon={Underline} testid="signature-btn-underline" />
        <span className="mx-1 h-4 w-px bg-[var(--border)]" />
        <ToolbarBtn onClick={insertLink} label="Link" icon={Link2} testid="signature-btn-link" />
        <ToolbarBtn onClick={insertBadgeExample} label="Badge" text="Badge" testid="signature-btn-badge" />
        <span className="mx-1 h-4 w-px bg-[var(--border)]" />
        <ToolbarBtn onClick={() => setPreviewOpen(true)} label="Preview" icon={Eye} testid="signature-btn-preview" />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!loading}
        suppressContentEditableWarning
        data-testid="signature-editor"
        className="min-h-[140px] px-3 py-2 border border-t-0 rounded-b-md text-[13px] leading-relaxed outline-none focus:ring-1 focus:ring-[var(--accent)]"
        style={{ borderColor: "var(--border)", background: "var(--paper)" }}
      />

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-[var(--ink-3)]">
          Signature is HTML-sanitised on save; disallowed tags are stripped automatically.
        </span>
        <button
          type="button" onClick={onSave} disabled={saving || loading}
          data-testid="signature-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50"
          style={{ background: "var(--ink)", color: "var(--paper)" }}
        >
          <Save size={13} /> {saving ? "Saving…" : "Save signature"}
        </button>
      </div>

      {previewOpen && (
        <SignaturePreview state={state} editorHtml={editorRef.current?.innerHTML || ""} onClose={() => setPreviewOpen(false)} />
      )}
    </section>
  );
}

function ToolbarBtn({ onClick, label, icon: Icon, text, testid }) {
  return (
    <button type="button" onClick={onClick} title={label} data-testid={testid}
            className="w-7 h-7 flex items-center justify-center rounded text-[var(--ink-2)] hover:bg-[var(--subtle)] hover:text-[var(--ink)]">
      {Icon ? <Icon size={12} /> : <span className="text-[10px] font-medium">{text}</span>}
    </button>
  );
}

function SignaturePreview({ state, editorHtml, onClose }) {
  const html = editorHtml || state.email_signature_html || "";
  const logo = state.email_signature_logo_url;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg shadow-xl overflow-hidden"
           style={{ background: "var(--paper)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="overline">Preview</div>
          <div className="text-[14px] text-[var(--ink)]">Signature as it will appear in outbound emails</div>
        </div>
        <div className="p-5 text-[13px] leading-relaxed text-[var(--ink)]" data-testid="signature-preview-body">
          <p className="mb-3 text-[var(--ink-2)]">Hi Jane,</p>
          <p className="mb-6 text-[var(--ink-2)]">Thanks for sending over the receipts — I&apos;ll get the BAS drafted this week and send it back for review.</p>
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #e5e0d6", color: "#3f3a2e", fontFamily: "Georgia, serif", fontSize: 13, lineHeight: 1.5 }}>
            {logo && <img src={logo} alt="" style={{ maxHeight: 56, maxWidth: 220, marginBottom: 8, display: "block" }} />}
            <div dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} data-testid="signature-preview-close"
                  className="px-4 py-1.5 rounded-md text-[12px] border hover:bg-[var(--subtle)]"
                  style={{ borderColor: "var(--border)" }}>Close</button>
        </div>
      </div>
    </div>
  );
}
